import * as React from 'react';
import { StyledModal } from './StyledModal';
import { DataService } from '../../services/DataService';
import { Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Code, IsUserSinfra, ReadFriendlyNumber } from '../../lib/utils';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { TKickback } from '../../services/API/Kickback';
import { Department } from '../../services/API/Departments';
import { calculateKickbackAmount, calculateReportedSumFromDeduction } from '../../lib/KickbackMath';

export interface SubmitKickbackReportModalProps {
  kickbacks: TKickback[];
  onDismiss():void;
  disabled:boolean;
  onSubmitted():void;
  period:string;
  departmentId?: number;
}

export function SubmitKickbackReportModal (props: SubmitKickbackReportModalProps) {
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [availableDepartments, setAvailableDepartments] = useState<Department[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  //const [comment, setComment] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    const loadDepartments = async () => {
      const res = await DataService.Departments.GetUserAssignedDepartments();
      if (cancelled) return;

      const departments = res.Content.response ?? [];
      setAvailableDepartments(departments);

      if (props.departmentId) {
        setSelectedDepartmentId(props.departmentId);
      } else if (departments.length === 1) {
        setSelectedDepartmentId(departments[0].id);
      } else {
        setSelectedDepartmentId(null);
      }
    };

    loadDepartments();

    return () => {
      cancelled = true;
    };
  }, [props.departmentId]);

  const onDismiss = () => {
    if(isSaving)
      return;
    props.onDismiss();
  }

  const onClickSubmit = async () => {
    setErrorMessage("");
    setSaving(true);
    const departmentId: number | undefined = availableDepartments.length > 1
      ? selectedDepartmentId ?? undefined
      : availableDepartments[0]?.id;
    const result = await DataService.Kickback.SubmitReport(props.period/*, comment,*/, props.kickbacks, departmentId);
    if(result.Code == Code.OK){
      props.onSubmitted();
      return;
    }
    setErrorMessage(result.Message);
    setSaving(false);
  }

  const { totalSum, totalKickback } = props.kickbacks.reduce((prev, curr) => {
    const reportedSum = calculateReportedSumFromDeduction(curr.sumExclMoms, curr.sumExclMomsChangesProps.value);
    return {
      totalKickback: prev.totalKickback + calculateKickbackAmount(reportedSum, curr.kickbackProcent),
      totalSum: prev.totalSum + reportedSum
    };
  }, { totalKickback: 0.0, totalSum: 0.0});
  return (
    <StyledModal 
    headerStyle={{backgroundColor:'#173c53',color:'white'}}
      onDismiss={onDismiss}
      title={t('kickback.previewModal.title').toString()}
      body={
        <Stack className='submit-modal-body' gap={3}>
          <table>
          <tr>
              <td>{t('kickback.list.period')}:</td>
              <td>{props.period}</td>
            </tr>
            <tr>
              <td>{t('kickback.previewModal.noOfRows')}:</td>
              <td>{props.kickbacks.length}</td>
            </tr>
            <tr>
              <td>{t('kickback.previewModal.totalSum')}:</td>
              <td>{ReadFriendlyNumber(totalSum) + " Kr"}</td>
            </tr>
            <tr>
              <td>{t('kickback.previewModal.totalKickback')}:</td>
              <td>{ReadFriendlyNumber(totalKickback) + " Kr"}</td>
            </tr>
          </table>
          {availableDepartments.length > 1 && (
            <Form.Group>
              <Form.Label>{t('general.department')}</Form.Label>
              <Form.Select
                value={selectedDepartmentId ?? ''}
                onChange={(ev) => setSelectedDepartmentId(ev.target.value === '' ? null : Number(ev.target.value))}
              >
                <option value="">{t('general.department')}</option>
                {availableDepartments.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.department}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {/* <Form.Group>
            <Form.Label>{t("general.comment")}:</Form.Label>
            <Form.Control 
              as="textarea" 
              value={comment}
              onChange={(ev) => setComment(ev.target.value)}
            />
          </Form.Group> */}
          {
            errorMessage &&
            <div className='error-message'>
              <span>{t('general.errorMsg')}:</span>
              {errorMessage}
            </div>
          }
          {/* {
            totalKickback == 0 &&  totalSum == 0 && !IsUserSinfra() && 
            <div className="info-message">
              <FontAwesomeIcon icon={faInfoCircle} />
              {t("kickback.zeroKickback")}
            </div>
          } */}
        </Stack>
      }
      footer={
      <Stack direction="horizontal" gap={2}>
        <Button style={{color:'#173C53'}} disabled={props.disabled || isSaving || props.kickbacks.length == 0 || IsUserSinfra() || (availableDepartments.length > 1 && selectedDepartmentId == null)} className="button button-submit" onClick={onClickSubmit}>
          {
            isSaving ?
            <Spinner animation="border" size={'sm'}/>
            :
            <><FontAwesomeIcon icon={faPaperPlane} style={{marginRight: 4}}/>{t('general.send')}</>
          }
        </Button>
        <Button disabled={isSaving} className="button button-cancel" onClick={onDismiss}>{t('general.close')}</Button>
      </Stack>}
    />
  );
}
